import { useState } from "react";
import { StatBox, PageFooter } from "./components";
import { formatHand, formatPlayerInfo } from "./utils";
import { WIDTH, HEIGHT } from "./constants";
import { PLAYER_HEADSHOT_FALLBACK } from "../../../config/constants";

export const PitcherPage = ({ player, stats, report, pageNumber, totalPages }) => {
  const [imageError, setImageError] = useState(false);
  const pitchingStats = stats?.pitching_stats?.[0] || {};
  const headshotUrl = stats?.headshot_url || PLAYER_HEADSHOT_FALLBACK;
  
  const throws = formatHand(player.throws || stats?.throws);
  const infoParts = formatPlayerInfo(stats);

  return (
    <div 
      className="bg-white relative"
      style={{ width: WIDTH, height: HEIGHT }}
      data-player-page
    >
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-600 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-emerald-900 ring-2 ring-white/30 flex-shrink-0">
            {!imageError ? (
              <img 
                src={headshotUrl} 
                alt={player.name} 
                className="w-full h-full object-cover object-top"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/50 text-2xl font-bold">
                {player.name?.charAt(0) || "?"}
              </div>
            )}
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-white">{player.name}</h1>
            <p className="text-emerald-200 text-sm">
              {player.role || "Pitcher"} • Throws: {throws}
            </p>
            {infoParts.length > 0 && (
              <p className="text-emerald-300/70 text-xs mt-0.5">
                {infoParts.join(" • ")}
              </p>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-white font-semibold">{report.teamName}</div>
          <div className="text-emerald-200 text-sm">{report.year} Season</div>
        </div>
      </div>

      <div className="px-8 py-6 flex gap-8">
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-emerald-800 mb-3 pb-1 border-b-2 border-emerald-100">
            Season Statistics
          </h2>
          <div className="grid grid-cols-6 gap-2 mb-6">
            <StatBox label="ERA" value={pitchingStats.era != null ? Number(pitchingStats.era).toFixed(2) : null} />
            <StatBox label="K%" value={pitchingStats.k_pct != null ? `${pitchingStats.k_pct.toFixed(0)}%` : null} />
            <StatBox label="BB%" value={pitchingStats.bb_pct != null ? `${pitchingStats.bb_pct.toFixed(0)}%` : null} />
            <StatBox label="IP" value={pitchingStats.ip} />
            <StatBox label="WAR" value={pitchingStats.war != null ? pitchingStats.war.toFixed(1) : null} />
          </div>

          {player.pitchArsenal?.length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-emerald-800 mb-3 pb-1 border-b-2 border-emerald-100">
                Pitch Arsenal
              </h2>
              <div className="flex flex-wrap gap-2 mb-6">
                {player.pitchArsenal.map((pitch, idx) => (
                  <div key={idx} className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <div className="text-sm font-semibold text-emerald-700">{pitch.name}</div>
                    {(pitch.velocityMin || pitch.velocityMax) && (
                      <div className="text-[10px] text-emerald-600">
                        {pitch.velocityMin && pitch.velocityMax 
                          ? `${pitch.velocityMin}-${pitch.velocityMax} mph`
                          : pitch.velocityMin 
                            ? `${pitch.velocityMin}+ mph` 
                            : `Up to ${pitch.velocityMax} mph`
                        }
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {player.writeup && (
            <>
              <h2 className="text-sm font-semibold text-emerald-800 mb-3 pb-1 border-b-2 border-emerald-100">
                Scouting Notes
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {player.writeup}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="w-72">
          <h2 className="text-sm font-semibold text-emerald-800 mb-3 pb-1 border-b-2 border-emerald-100">
            Pitch Tendencies
          </h2>
          
          <div className="space-y-3">
            {(player.firstPitchRHH || player.firstPitchLHH) && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">First Pitch</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-gray-400">vs RHH:</span>
                    <div className="text-sm font-medium text-gray-900">{player.firstPitchRHH || "—"}</div>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">vs LHH:</span>
                    <div className="text-sm font-medium text-gray-900">{player.firstPitchLHH || "—"}</div>
                  </div>
                </div>
              </div>
            )}

            {(player.twoStrikePitchRHH || player.twoStrikePitchLHH) && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Two Strikes</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-[10px] text-gray-400">vs RHH:</span>
                    <div className="text-sm font-medium text-gray-900">{player.twoStrikePitchRHH || "—"}</div>
                  </div>
                  <div>
                    <span className="text-[10px] text-gray-400">vs LHH:</span>
                    <div className="text-sm font-medium text-gray-900">{player.twoStrikePitchLHH || "—"}</div>
                  </div>
                </div>
              </div>
            )}

            {player.timeToPlate && (
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-gray-500">Time to Plate:</span>
                <span className="text-sm font-medium text-gray-900">{player.timeToPlate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <PageFooter pageNumber={pageNumber} totalPages={totalPages} />
    </div>
  );
};
